import { useState, useMemo } from "react";
import { Restaurant } from "@/lib/supabase";
import { Phone, CreditCard, Truck, Ticket, Facebook, Globe, MapPin } from "lucide-react";
import { cn, ensureExternalUrl } from "@/lib/utils";
import { useGoogleMapsApiKey } from "@/hooks/useGoogleMapsApiKey";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface RestaurantCardInlineProps {
  restaurant: Restaurant;
}

export function RestaurantCardInline({
  restaurant
}: RestaurantCardInlineProps) {
  const [isMapOpen, setIsMapOpen] = useState(false);
  const { apiKey } = useGoogleMapsApiKey(isMapOpen);

  // Helper function to convert Google Maps URL to embed format
  const convertToEmbedUrl = useMemo(() => {
    return (url: string | null, restaurantName?: string, address?: string, city?: string): string => {
      // Don't generate URL without API key
      if (!apiKey) return "";
      if (!url) return "";

      // If already an embed URL, return as is
      if (url.includes('/embed')) return url;

      // Try to extract place ID from URL - this shows the info window
      const placeIdMatch = url.match(/!1s([A-Za-z0-9_-]+)(?:!|$)/);
      if (placeIdMatch) {
        return `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=place_id:${placeIdMatch[1]}`;
      }

      // Try to use the place name from the URL - this also shows info
      const placeMatch = url.match(/place\/([^/]+)/);
      if (placeMatch) {
        const placeName = decodeURIComponent(placeMatch[1].replace(/\+/g, ' '));
        const query = encodeURIComponent(placeName);
        return `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${query}`;
      }

      // Try restaurant name with address
      if (restaurantName && address && city) {
        const query = encodeURIComponent(`${restaurantName}, ${address}, ${city}`);
        return `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${query}`;
      }

      // Fall back to address only
      if (address && city) {
        const query = encodeURIComponent(`${address}, ${city}`);
        return `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${query}`;
      }

      // Try to extract coordinates from the URL as last resort
      const coordMatch = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (coordMatch) {
        const [, lat, lng] = coordMatch;
        return `https://www.google.com/maps/embed/v1/view?key=${apiKey}&center=${lat},${lng}&zoom=15`;
      }
      return "";
    };
  }, [apiKey]);

  const embedUrl = useMemo(() => {
    return convertToEmbedUrl(
      (restaurant as any).google_maps_url,
      restaurant.name,
      restaurant.address,
      restaurant.city
    );
  }, [convertToEmbedUrl, restaurant]);

  return (
    <div className="p-4 border-b bg-muted/20 restaurant-card-content">
      <div className="flex flex-col md:flex-row gap-4 md:items-start">
        {restaurant.logo_url && (
          <div className="flex-shrink-0 mx-auto md:mx-0 w-[120px] h-[120px] bg-muted/30 rounded">
            <img 
              src={restaurant.logo_url} 
              alt={`${restaurant.name} logo`} 
              className="h-[120px] w-[120px] rounded object-cover border" 
              width={120}
              height={120}
              loading="lazy"
              decoding="async"
            />
          </div>
        )}
        
        <div className="flex-1 space-y-2 min-w-0">
          {restaurant.address && restaurant.city && (
            <div className="text-sm break-words my-0 mx-0 py-0">
              <span className="font-medium text-base">Cím:</span>{" "}
              {(restaurant as any).google_maps_url ? (
                <span className="inline-flex items-center gap-1">
                  <button onClick={() => setIsMapOpen(true)} className="hover:underline text-left text-base">
                    {restaurant.address}, {restaurant.city}
                  </button>
                  <MapPin size={21} className="cursor-pointer hover:text-primary inline-block" onClick={() => setIsMapOpen(true)} />
                </span>
              ) : (
                <span>{restaurant.address}, {restaurant.city}</span>
              )}
            </div>
          )}
          
          {(restaurant.phone || (restaurant as any).phone_2) && (
            <div className="text-sm tour-phone flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1">
                <span className="font-medium text-base">Telefon:</span>{" "}
                {restaurant.phone && (
                  <a href={`tel:${restaurant.phone}`} className="text-base text-price font-bold">
                    {restaurant.phone}
                  </a>
                )}
                {restaurant.phone && (restaurant as any).phone_2 && (
                  <span className="text-muted-foreground"> vagy </span>
                )}
                {(restaurant as any).phone_2 && (
                  <a href={`tel:${(restaurant as any).phone_2}`} className="text-base text-price font-bold">
                    {(restaurant as any).phone_2}
                  </a>
                )}
              </div>
              <a 
                href={`tel:${restaurant.phone || (restaurant as any).phone_2}`} 
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-white text-sm font-medium transition-colors hover:opacity-90"
                style={{ backgroundColor: '#3D5FC7' }}
              >
                <Phone size={16} />
                Rendelek
              </a>
            </div>
          )}
          
          {restaurant.menu_start_time && restaurant.menu_end_time && (
            <div className="text-sm">
              <span className="font-medium text-base">Ebédidő:</span>{" "}
              <span className="text-base">{restaurant.menu_start_time} - {restaurant.menu_end_time} óráig</span>
            </div>
          )}
          
          {(restaurant.dine_in_price || restaurant.takeaway_price || restaurant.delivery_price) && (
            <div className="text-sm flex flex-wrap gap-x-4 gap-y-1">
              {restaurant.dine_in_price && (
                <div>
                  <span className="font-medium text-base">Helyben fogyasztva:</span>{" "}
                  <span className="text-price font-semibold text-base">{restaurant.dine_in_price} Ft</span>
                </div>
              )}
              {restaurant.takeaway_price && (
                <div>
                  <span className="font-medium text-base">Elviteles ár:</span>{" "}
                  <span className="text-price font-semibold text-base">{restaurant.takeaway_price} Ft</span>
                </div>
              )}
              {restaurant.delivery_price && (
                <div>
                  <span className="font-medium text-base">Kiszállítva:</span>{" "}
                  <span className="text-price font-semibold text-base">{restaurant.delivery_price} Ft</span>
                </div>
              )}
            </div>
          )}
          
          <div className="flex items-center gap-2 flex-wrap">
            {restaurant.accepts_card && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <CreditCard size={21} />
                <span className="text-sm">Bankkártya</span>
              </div>
            )}
            
            {restaurant.delivery_available && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Truck size={21} />
                <span className="text-sm">Kiszállítás</span>
              </div>
            )}
            
            {restaurant.accepts_vouchers && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Ticket size={21} />
                <span className="text-sm">Utalvány</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Facebook and Website icons on the right side */}
        <TooltipProvider delayDuration={200} skipDelayDuration={100}>
          <div className="flex flex-col items-center gap-1 flex-shrink-0">
            <Tooltip>
              <TooltipTrigger asChild>
                {restaurant.facebook_url ? (
                  <a 
                    href={ensureExternalUrl(restaurant.facebook_url) || '#'} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    referrerPolicy="no-referrer" 
                    className={cn(
                      "inline-flex items-center justify-center h-10 w-10 rounded-md hover:bg-accent", 
                      "text-foreground hover:text-primary"
                    )}
                  >
                    <Facebook size={31} />
                  </a>
                ) : (
                  <span className="inline-flex items-center justify-center h-10 w-10 text-muted-foreground/40 cursor-not-allowed">
                    <Facebook size={31} />
                  </span>
                )}
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-semibold">Facebook</p>
                <p className="text-xs">Kattints és irány az étterem Facebook oldala!</p>
              </TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                {restaurant.website ? (
                  <a 
                    href={ensureExternalUrl(restaurant.website) || '#'} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    referrerPolicy="no-referrer" 
                    className={cn(
                      "inline-flex items-center justify-center h-10 w-10 rounded-md hover:bg-accent", 
                      "text-foreground hover:text-primary"
                    )}
                  >
                    <Globe size={31} />
                  </a>
                ) : (
                  <span className="inline-flex items-center justify-center h-10 w-10 text-muted-foreground/40 cursor-not-allowed">
                    <Globe size={31} />
                  </span>
                )}
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-semibold">Weboldal</p>
                <p className="text-xs">Kattints és irány az étterem weboldala!</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </div>

      <Dialog open={isMapOpen} onOpenChange={setIsMapOpen}>
        <DialogContent className="max-w-[95vw] w-[95vw] h-[90vh] p-0 gap-0 flex flex-col">
          <DialogHeader className="px-6 py-3 border-b flex-shrink-0">
            <DialogTitle>{restaurant.name} - Térkép</DialogTitle>
          </DialogHeader>
          <div className="flex-1 relative">
            {embedUrl ? (
              <iframe 
                src={embedUrl} 
                className="absolute inset-0 w-full h-full" 
                style={{ border: 0 }} 
                allowFullScreen 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade" 
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
                Térkép betöltése...
              </div>
            )}
            {/* Info bubble */}
            <div className="absolute bottom-4 left-4 bg-background/95 backdrop-blur-sm rounded-lg shadow-lg border p-4 max-w-xs z-10">
              <h3 className="font-semibold text-base mb-2">{restaurant.name}</h3>
              {restaurant.address && restaurant.city && (
                <p className="text-sm text-muted-foreground mb-2">
                  {restaurant.address}, {restaurant.city}
                </p>
              )}
              <div className="flex items-center gap-3">
                {restaurant.phone && (
                  <a 
                    href={`tel:${restaurant.phone}`} 
                    className="flex items-center gap-1.5 text-sm text-primary hover:underline"
                  >
                    <Phone size={16} />
                    <span>{restaurant.phone}</span>
                  </a>
                )}
                <a 
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${restaurant.address || ''}, ${restaurant.city || ''}`)}`} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center gap-1.5 text-sm text-primary hover:underline"
                >
                  <MapPin size={16} />
                  <span>Útvonal</span>
                </a>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
